import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  isAiMessage,
  isUserMessage,
  type ChatMessage,
  type ChatMessageSource,
} from '../../../types/chatMessage';
import { formatTimeOfDay } from '../../../utils/formatUtils';
import { LiksChatMessage } from '../LiksChatMessage/LiksChatMessage';
import type { LiksChatMessageDocumentPreviewRenderProps } from '../LiksChatMessage/LiksChatMessageSources';
import './LiksChatMessageList.css';

export type LiksChatMessageListProps = {
  messages: ChatMessage[];
  header?: React.ReactNode;
  stickToBottom?: boolean;
  currentUserId?: string;
  locale: string;
  copyLabel: string;
  copiedLabel: string;
  retryLabel: string;
  downloadImageLabel: string;
  downloadAttachmentLabel: string;
  expandImageLabel: string;
  closeImagePreviewLabel: string;
  imageLoadingLabel: string;
  assistantReplyingLabel: string;
  generatedImageAltLabel: string;
  imageGenerationFailedLabel?: string;
  enableSaveToGallery?: boolean;
  saveToGalleryLabel?: string;
  sourcesLabel: string;
  sourcesEmptyLabel: string;
  getSourceTypeLabel: (sourceType: string | undefined) => string;
  openDocumentLabel: string;
  openTrainingExampleLabel?: string;
  documentPreviewCloseLabel: string;
  documentPreviewLoadingLabel: string;
  documentPreviewErrorLabel: string;
  documentPreviewInaccessibleLabel?: string;
  documentPreviewUnavailableLabel: string;
  sourceTypeColumnLabel: string;
  sourceFileColumnLabel: string;
  sourceUrlColumnLabel: string;
  sourceInaccessibleLabel: string;
  sourceTrainingDeletedLabel?: string;
  disableRetry?: boolean;
  onRetry?: (messageId: string) => void;
  enableRetry?: boolean;
  enableSources?: boolean;
  enableAttachments?: boolean;
  fallbackKnowledgeBaseId?: string;
  knowledgeBases?: ReadonlyArray<{ id: string; name: string }>;
  sourcesHint?: string;
  onOpenTrainingExample?: (source: ChatMessageSource) => void;
  renderDocumentPreview?: (
    props: LiksChatMessageDocumentPreviewRenderProps,
  ) => ReactNode;
  improveLabel?: string;
  canImproveMessage?: (message: ChatMessage) => boolean;
  onImproveMessage?: (messageId: string) => void;
};

export function LiksChatMessageList({
  messages,
  header,
  stickToBottom = true,
  currentUserId,
  locale,
  copyLabel,
  copiedLabel,
  retryLabel,
  downloadImageLabel,
  downloadAttachmentLabel,
  expandImageLabel,
  closeImagePreviewLabel,
  imageLoadingLabel,
  assistantReplyingLabel,
  generatedImageAltLabel,
  imageGenerationFailedLabel,
  enableSaveToGallery = false,
  saveToGalleryLabel,
  sourcesLabel,
  sourcesEmptyLabel,
  getSourceTypeLabel,
  openDocumentLabel,
  openTrainingExampleLabel,
  documentPreviewCloseLabel,
  documentPreviewLoadingLabel,
  documentPreviewErrorLabel,
  documentPreviewInaccessibleLabel,
  documentPreviewUnavailableLabel,
  sourceTypeColumnLabel,
  sourceFileColumnLabel,
  sourceUrlColumnLabel,
  sourceInaccessibleLabel,
  sourceTrainingDeletedLabel,
  disableRetry = false,
  onRetry,
  enableRetry = true,
  enableSources = true,
  enableAttachments = true,
  fallbackKnowledgeBaseId,
  knowledgeBases,
  sourcesHint,
  onOpenTrainingExample,
  renderDocumentPreview,
  improveLabel,
  canImproveMessage,
  onImproveMessage,
}: LiksChatMessageListProps) {
  const listEndRef = useRef<HTMLDivElement>(null);

  const latestAiMessageId = useMemo(() => {
    if (!enableRetry) {
      return undefined;
    }
    const aiMessages = messages.filter((message) => isAiMessage(message));
    return aiMessages.at(-1)?.id;
  }, [enableRetry, messages]);

  useEffect(() => {
    if (!stickToBottom) {
      return;
    }

    listEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, stickToBottom]);

  return (
    <div className="liks-chat-message-list" role="log" aria-live="polite">
      {header}
      {messages.map((message) => {
        const showImprove =
          improveLabel != null &&
          canImproveMessage?.(message) === true &&
          onImproveMessage != null;

        return (
          <LiksChatMessage
            key={message.id}
            message={message}
            formattedTime={formatTimeOfDay(message.sentAt, locale)}
            copyLabel={copyLabel}
            copiedLabel={copiedLabel}
            retryLabel={retryLabel}
            downloadImageLabel={downloadImageLabel}
            downloadAttachmentLabel={downloadAttachmentLabel}
            expandImageLabel={expandImageLabel}
            closeImagePreviewLabel={closeImagePreviewLabel}
            imageLoadingLabel={imageLoadingLabel}
            assistantReplyingLabel={assistantReplyingLabel}
            generatedImageAltLabel={generatedImageAltLabel}
            imageGenerationFailedLabel={imageGenerationFailedLabel}
            enableSaveToGallery={enableSaveToGallery}
            saveToGalleryLabel={saveToGalleryLabel}
            sourcesLabel={sourcesLabel}
            sourcesEmptyLabel={sourcesEmptyLabel}
            sourceTypeColumnLabel={sourceTypeColumnLabel}
            sourceFileColumnLabel={sourceFileColumnLabel}
            sourceUrlColumnLabel={sourceUrlColumnLabel}
            sourceInaccessibleLabel={sourceInaccessibleLabel}
            getSourceTypeLabel={getSourceTypeLabel}
            openDocumentLabel={openDocumentLabel}
            openTrainingExampleLabel={openTrainingExampleLabel}
            documentPreviewCloseLabel={documentPreviewCloseLabel}
            documentPreviewLoadingLabel={documentPreviewLoadingLabel}
            documentPreviewErrorLabel={documentPreviewErrorLabel}
            documentPreviewInaccessibleLabel={documentPreviewInaccessibleLabel}
            documentPreviewUnavailableLabel={documentPreviewUnavailableLabel}
            sourceTrainingDeletedLabel={sourceTrainingDeletedLabel}
            showRetry={enableRetry && message.id === latestAiMessageId}
            disableRetry={disableRetry}
            onRetry={
              enableRetry && message.id === latestAiMessageId
                ? () => onRetry?.(message.id)
                : undefined
            }
            isCurrentUser={
              isUserMessage(message) && message.author.id === currentUserId
            }
            enableSources={enableSources}
            enableAttachments={enableAttachments}
            fallbackKnowledgeBaseId={fallbackKnowledgeBaseId}
            knowledgeBases={knowledgeBases}
            sourcesHint={sourcesHint}
            onOpenTrainingExample={onOpenTrainingExample}
            renderDocumentPreview={renderDocumentPreview}
            showImprove={showImprove}
            improveLabel={improveLabel}
            onImprove={
              showImprove ? () => onImproveMessage(message.id) : undefined
            }
          />
        );
      })}
      <div ref={listEndRef} className="liks-chat-message-list__anchor" />
    </div>
  );
}
